/* VIZZYO BF2026 - design5 "smart" layer (2026-10-24). Loaded by vzy-bf-config only while the campaign runs.
   1. Smart next tier (drawer + /cart): [data-vzy-bfp-add] adds the suggested camera with the theme's own flow
      (POST /cart/add.js with sections -> drawer.renderContents / cart page sections -> cart:change -> vzy-bf-code.js
      puts the next tier code on the cart).
   2. /cart "+1 cameră" ([data-vzy-bfp-plus]): clicks that line's "+" on the cart page (cart.js does the rest).
   3. PDP "Orice model se combină" ([data-vzy-bfc]): follows the chosen quantity (vzy:product-state) and the chosen
      card (second camera or accessory), recomputes the tier, total and saving exactly like the tier code (per line,
      round(line x pct); an accessory keeps this camera's tier) and adds both in one request.
   No timers, no layout reads in loops; text is replaced in place (same length class), so nothing jumps. */
(() => {
  if (window.__vzyBfSmart) return;
  window.__vzyBfSmart = true;

  const root = document.documentElement;
  const ended = () => root.classList.contains('vzy-bf-ended');
  const cartUrl = window.routes?.cart_url || '/cart';
  const addUrl = window.routes?.cart_add_url || '/cart/add';

  const formatMoney = (cents) => {
    const value = (Math.round(Number(cents) || 0) / 100).toFixed(2).split('.');
    return `${value[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${value[1]} lei`;
  };
  const tierFor = (tiers, cameras) => {
    let match = null;
    tiers.forEach((tier) => { if (cameras >= tier.min) match = tier; });
    return match;
  };
  const lineOff = (cents, pct) => Math.round((cents * pct) / 100);
  const plural = (n) => (n === 1 ? 'o cameră' : n % 100 === 0 || n % 100 >= 20 ? `${n} de camere` : `${n} camere`);

  // ---------------------------------------------------------------------------------------------------------------
  // Shared add-to-cart (the theme's flow, so the drawer, the bubble and vzy-bf-code.js all react as for product-form)
  // ---------------------------------------------------------------------------------------------------------------
  const cartPageSections = () => {
    const items = document.querySelector('cart-items');
    if (!items || typeof items.getSectionsToRender !== 'function' || !document.getElementById('main-cart-items')) return [];
    try {
      return items.getSectionsToRender().filter((entry) => entry.section && document.getElementById(entry.id));
    } catch (error) {
      return [];
    }
  };

  const addItems = (items) => {
    const page = cartPageSections();
    const drawer = page.length ? null : document.querySelector('cart-drawer');
    const useDrawer = Boolean(drawer && typeof drawer.renderContents === 'function' && typeof drawer.getSectionsToRender === 'function');
    const sections = useDrawer ? drawer.getSectionsToRender().map((section) => section.id) : page.map((entry) => entry.section);
    return fetch(`${addUrl}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items, sections: Array.from(new Set(sections)), sections_url: window.location.pathname })
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok || data?.status) throw new Error(data?.description || data?.message || 'add');
        if (useDrawer) {
          drawer.classList.remove('is-empty');
          drawer.renderContents(data);
        } else if (page.length) {
          page.forEach((entry) => {
            const html = data?.sections?.[entry.section];
            if (!html) return;
            const source = new DOMParser().parseFromString(html, 'text/html').querySelector(entry.selector);
            const target = document.getElementById(entry.id)?.querySelector(entry.selector) || document.getElementById(entry.id);
            if (source && target) target.innerHTML = source.innerHTML;
          });
          document.querySelector('cart-items')?.classList.remove('is-empty');
        } else {
          window.location.href = cartUrl;
          return data;
        }
        // Same announcements as the theme's product-form: vzy-bf-code.js re-reads the cart and applies the tier code.
        fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => response.json())
          .then((cart) => document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart, sectionId: 'vzy-bf-smart' } })))
          .catch(() => {});
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'vzy-bf-smart', cartData: data });
        }
        return data;
      });
  };

  const busy = (button, text) => {
    const label = button.querySelector('[data-vzy-bfc-label]') || button;
    const original = label.textContent;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    label.textContent = text;
    return (finalText, delay) => {
      const restore = () => {
        if (!button.isConnected) return;
        button.disabled = false;
        button.removeAttribute('aria-busy');
        label.textContent = original;
      };
      if (finalText) {
        button.removeAttribute('aria-busy');
        label.textContent = finalText;
        window.setTimeout(restore, delay || 2600);
      } else {
        restore();
      }
    };
  };

  // ---------------------------------------------------------------------------------------------------------------
  // 1 + 2. Drawer / cart page panel
  // ---------------------------------------------------------------------------------------------------------------
  document.addEventListener('click', (event) => {
    const add = event.target.closest?.('[data-vzy-bfp-add]');
    if (add instanceof HTMLButtonElement && !add.disabled) {
      event.preventDefault();
      const id = Number(add.dataset.vzyBfpAdd);
      if (!id || ended()) return;
      const release = busy(add, 'Se adaugă…');
      addItems([{ id, quantity: 1 }])
        .then(() => release())
        .catch(() => release('Indisponibil'));
      return;
    }
    const plus = event.target.closest?.('[data-vzy-bfp-plus]');
    if (plus instanceof HTMLButtonElement && !plus.disabled) {
      const line = Number(plus.dataset.vzyBfpPlus);
      const target = document.querySelector(`#CartItem-${line} quantity-input button[name="plus"]`);
      if (!(target instanceof HTMLButtonElement) || target.disabled) return;
      event.preventDefault();
      busy(plus, 'Se adaugă…');
      target.click();
    }
  });

  // ---------------------------------------------------------------------------------------------------------------
  // 3. PDP "Orice model se combină"
  // ---------------------------------------------------------------------------------------------------------------
  const readJson = (element, fallback) => {
    try {
      return JSON.parse(element?.textContent || '');
    } catch (error) {
      return fallback;
    }
  };

  const setupCombo = (box) => {
    if (box.__vzyBfc) return;
    box.__vzyBfc = true;
    const tiers = (readJson(box.querySelector('[data-vzy-bfc-tiers]'), []) || [])
      .map((tier) => ({ min: Number(tier.min), pct: Number(tier.pct) }))
      .filter((tier) => tier.min > 0 && tier.pct > 0)
      .sort((a, b) => a.min - b.min);
    const variantPrices = readJson(box.querySelector('[data-vzy-bfc-variants]'), {}) || {};
    const state = {
      variantId: Number(box.dataset.variantId),
      price: Number(box.dataset.price),
      quantity: 1,
      available: true
    };
    const name = box.dataset.name || '';
    const lead = box.querySelector('[data-vzy-bfc-lead]');
    const rule = box.querySelector('[data-vzy-bfc-rule]');
    const pair = box.querySelector('[data-vzy-bfc-pair]');
    const total = box.querySelector('[data-vzy-bfc-total]');
    const save = box.querySelector('[data-vzy-bfc-save]');
    const button = box.querySelector('[data-vzy-bfc-add]');

    const picked = () => box.querySelector('.vzy-bfc__radio:checked') || box.querySelector('.vzy-bfc__radio');

    const paint = () => {
      const pick = picked();
      if (!pick || !tiers.length) return;
      // A second camera moves both to the next tier; an accessory keeps this camera's tier and its own price.
      const isCamera = pick.dataset.camera !== 'false';
      const cameras = state.quantity + (isCamera ? 1 : 0);
      const tier = tierFor(tiers, cameras);
      const pct = tier ? tier.pct : 0;
      const pairTier = tierFor(tiers, state.quantity + 1);
      const pairPct = pairTier ? pairTier.pct : 0;
      // Per-line rounding: this camera's line (quantity x price) and the second camera's line.
      const mainLine = state.price * state.quantity;
      const pickPrice = Number(pick.dataset.price);
      const off = lineOff(mainLine, pct) + (isCamera ? lineOff(pickPrice, pct) : 0);
      if (lead) lead.textContent = isCamera ? 'Încă o cameră din alt model:' : 'Plus un accesoriu util:';
      if (rule) {
        rule.textContent = !isCamera ? 'în același colet'
          : cameras === 2 ? `−${pct}% la amândouă` : `${plural(cameras)}, −${pct}% la toate`;
      }
      if (pair) pair.textContent = `${state.quantity > 1 ? `${state.quantity} × ` : ''}${name} + ${pick.dataset.name || ''}`;
      if (total) total.textContent = formatMoney(mainLine + pickPrice - off);
      if (save) {
        save.textContent = formatMoney(off);
        // Pachete: a camera + an accessory is below the first package, so there is nothing saved to show.
        const saveWrap = save.closest('.vzy-bfc__save');
        if (saveWrap) saveWrap.hidden = off <= 0;
      }
      box.querySelectorAll('.vzy-bfc__radio[data-camera="true"]').forEach((radio) => {
        const unit = radio.closest('.vzy-bfc__opt')?.querySelector('[data-vzy-bfc-unit]');
        const price = Number(radio.dataset.price);
        if (unit) unit.textContent = formatMoney(price - lineOff(price, pairPct));
      });
      if (button) button.disabled = !state.available;
      box.dataset.vzyBfcPct = String(pct);
    };

    box.addEventListener('change', (event) => {
      if (event.target.closest?.('.vzy-bfc__radio')) paint();
    });

    // The package cards / quantity stepper of the same product section.
    document.addEventListener('vzy:product-state', (event) => {
      const detail = event.detail || {};
      const sectionId = box.dataset.sectionId;
      if (sectionId && detail.sectionId && detail.sectionId !== sectionId) return;
      const quantity = Math.max(1, Math.min(99, Number(detail.quantity) || 1));
      const variantId = Number(detail.variantId) || state.variantId;
      const price = Number(variantPrices[variantId]);
      Object.assign(state, {
        quantity,
        variantId,
        price: Number.isFinite(price) && price > 0 ? price : state.price,
        available: detail.available !== false
      });
      paint();
    });

    button?.addEventListener('click', () => {
      const pick = picked();
      if (!pick || button.disabled || ended()) return;
      const release = busy(button, 'Se adaugă…');
      addItems([
        { id: state.variantId, quantity: state.quantity },
        { id: Number(pick.value), quantity: 1 }
      ])
        .then(() => release())
        .catch(() => release('Stoc insuficient, alege alt model', 3200));
    });

    paint();
  };

  const start = () => document.querySelectorAll('[data-vzy-bfc]').forEach(setupCombo);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', start);
})();
